import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Lock, Play } from "lucide-react";
import api from "@/lib/api";
import { euro } from "@/lib/format";
import { filterCss } from "@/lib/videoFilters";

// Renders either a locked placeholder (never loads original) or the revealed image.
const PhotoMedia = ({ photo, className = "", feed = false, active = false }) => {
  const unlocked = photo.status === "unlocked" || photo.is_sender || photo.purchased || photo.is_free;
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [frameIdx, setFrameIdx] = useState(0);
  const videoRef = useRef(null);
  const frames = photo.blur_frames && photo.blur_frames.length > 1 ? photo.blur_frames : null;

  useEffect(() => {
    if (unlocked || !frames) return;
    const id = setInterval(() => setFrameIdx((i) => (i + 1) % frames.length), 650);
    return () => clearInterval(id);
  }, [unlocked, frames]);

  // Feed autoplay: play the active video, pause others. Apply trim loop bounds + volume.
  useEffect(() => {
    if (!feed) return;
    const v = videoRef.current;
    if (!v) return;
    const ts = Number(photo.trim_start) || 0;
    const te = Number(photo.trim_end) || 0;
    const hasSound = !!photo.sound_preview_url;
    // If a music track plays over the video, keep video muted; otherwise use original volume.
    v.muted = hasSound ? true : (Number(photo.original_volume) === 0);
    if (!hasSound) v.volume = Number(photo.original_volume ?? 1);
    const onTime = () => {
      if (te > ts && (v.currentTime >= te || v.currentTime < ts - 0.05)) {
        try { v.currentTime = ts; } catch (e) {}
      }
    };
    const onLoaded = () => { if (ts > 0) { try { v.currentTime = ts; } catch (e) {} } };
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onLoaded);
    if (active) { v.play().catch(() => {}); }
    else { try { v.pause(); v.currentTime = ts; } catch (e) {} }
    return () => { v.removeEventListener("timeupdate", onTime); v.removeEventListener("loadedmetadata", onLoaded); };
  }, [active, feed, src, photo.trim_start, photo.trim_end, photo.sound_preview_url, photo.original_volume]);

  useEffect(() => {
    let cancel = false;
    if (unlocked) {
      setLoading(true);
      api
        .get(`/photos/${photo.id}/original`)
        .then(({ data }) => {
          if (cancel) return;
          if (data.media_url) {
            // Média servi en streaming par le backend (stockage objet) — jeton requis pour <img>/<video>
            const token = localStorage.getItem("verrouille_token");
            setSrc(`${process.env.REACT_APP_BACKEND_URL}${data.media_url}?auth=${encodeURIComponent(token || "")}`);
          } else {
            setSrc(data.image_base64);
          }
        })
        .catch(() => {})
        .finally(() => !cancel && setLoading(false));
    }
    return () => {
      cancel = true;
    };
  }, [photo.id, unlocked]);

  if (unlocked) {
    const isVideo = photo.media_type === "video";
    return (
      <div className={`relative w-full h-full bg-secondary ${className}`}>
        {src ? (
          isVideo ? (
            <video
              ref={videoRef}
              data-testid="unlocked-video"
              src={src}
              controls={!feed}
              autoPlay={feed}
              loop={feed}
              muted={feed}
              playsInline
              style={photo.filter ? { filter: filterCss(photo.filter) } : undefined}
              className="w-full h-full object-cover bg-black"
            />
          ) : (
            <motion.img
              data-testid="unlocked-photo-image"
              initial={{ opacity: 0, filter: "blur(12px)" }}
              animate={{ opacity: 1, filter: "blur(0px)" }}
              transition={{ duration: 0.6 }}
              src={src}
              alt={photo.caption || "photo"}
              className="w-full h-full object-cover"
            />
          )
        ) : (
          <div className="w-full h-full grid place-items-center text-muted-foreground text-sm">
            {loading ? "Chargement…" : ""}
          </div>
        )}
      </div>
    );
  }

  // Locked placeholder — original image is NEVER fetched; show server-side blurred teaser if available
  return (
    <div className={`relative w-full h-full overflow-hidden ${className}`}>
      {frames ? (
        frames.map((f, i) => (
          <img
            key={i}
            src={f}
            alt=""
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
            style={{ filter: "blur(12px)", transform: "scale(1.18)", opacity: i === frameIdx ? 1 : 0 }}
          />
        ))
      ) : photo.blur ? (
        <img src={photo.blur} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "blur(12px)", transform: "scale(1.18)" }} />
      ) : (
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(140px circle at 28% 28%, hsla(214,100%,62%,0.55), transparent 60%), radial-gradient(160px circle at 78% 72%, hsla(0,92%,58%,0.5), transparent 60%), linear-gradient(135deg, hsl(222 30% 9%), hsl(222 40% 14%))",
            filter: "blur(2px)",
          }}
        />
      )}
      <div className="absolute inset-0 bg-[hsla(222,47%,11%,0.45)]" />
      {photo.media_type === "video" && (
        <div className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-black/55 backdrop-blur px-2 py-0.5 text-[10px] font-medium text-white" data-testid="locked-video-badge">
          <Play className="h-3 w-3 fill-white" /> Vidéo
        </div>
      )}
      <div className="absolute inset-0 grid place-items-center">
        <div className="flex flex-col items-center gap-2 text-white/90">
          <div className="h-14 w-14 rounded-2xl bg-white/10 border border-white/15 backdrop-blur grid place-items-center shadow-[0_0_22px_rgba(45,132,255,0.55),0_0_48px_rgba(45,132,255,0.22)]">
            <Lock className="h-6 w-6 text-[hsl(214_100%_62%)]" />
          </div>
          <span className="text-xs font-medium tracking-wide">{euro(photo.price)}</span>
        </div>
      </div>
    </div>
  );
};

export default PhotoMedia;
